import { Dialog, LinearProgress } from '@mui/material'
import { useState } from 'react'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { DropAlert } from './DropAlert'
import { DropPaper } from './DropPaper'
import { useSharedState } from '../waxplorer/Waxplorer'
import MainButton from '../common/util/input/MainButton'
import InputField from '../common/util/InputField'
import { inputStyles } from '../common/styles'

export const InstantMintAndSendDialog = ({ open, setOpen, drop }) => {
    const MAX_MINTS_PER_TRANSACTION = 100

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [errors, setErrors] = useState(null)
    const [transactionIds, setTransactionIds] = useState(null)
    const [isMinting, setIsMinting] = useState(false)
    const [currentDisplayAction, setCurrentDisplayAction] = useState(0)

    const [totalAssetAmountToMint, setTotalAssetAmountToMint] = useState(1)
    const [totalMintedSuccessfully, setTotalMintedSuccessfully] = useState(0)

    const [receiver, setReceiver] = useState(userName ? userName : '')

    const close = () => {
        if (!isMinting) {
            setOpen(false)
            setCurrentDisplayAction(0)
            setTotalAssetAmountToMint(1)
            setTotalMintedSuccessfully(0)
            setReceiver(userName ? userName : '')
            setErrors(null)
            setTransactionIds(null)
        }
    }

    const mintAndSendAssets = async () => {
        setErrors(null)
        setCurrentDisplayAction(0)
        setTransactionIds(null)
        setIsMinting(true)

        const tempTransactionIds = []
        const tempErrors = []

        let currentAction = 0
        let assetsMintedAmountThisRound = 0
        const assetsToMintThisRound =
            totalAssetAmountToMint - totalMintedSuccessfully

        while (assetsMintedAmountThisRound < assetsToMintThisRound) {
            const amountToMintThisTransaction = Math.min(
                MAX_MINTS_PER_TRANSACTION,
                assetsToMintThisRound - assetsMintedAmountThisRound,
            )

            const actions = [getBoostAction(activeUser)]

            const data = {
                authorized_account: userName,
                drop_id: drop.dropId,
                amount: amountToMintThisTransaction,
                receiver: receiver,
                collection_name: drop.collection,
            }

            const action = {
                account: 'nfthivedrops',
                name: 'orderpromo',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: data,
            }

            actions.push(action)

            let tempError = null
            try {
                const result = await activeUser.session.transact(
                    {
                        actions: actions,
                    },
                    {
                        expireSeconds: 300,
                        blocksBehind: 0,
                    },
                )
                tempTransactionIds.push(result?.response?.transaction_id)
            } catch (e) {
                tempError = e.message
                tempErrors.push(tempError)
            }
            if (!tempError) {
                setTotalMintedSuccessfully(
                    totalMintedSuccessfully + amountToMintThisTransaction,
                )
            }
            assetsMintedAmountThisRound =
                assetsMintedAmountThisRound + amountToMintThisTransaction
            currentAction++
            setCurrentDisplayAction(currentAction)
            if (assetsMintedAmountThisRound < assetsToMintThisRound) {
                await sleep(2000)
            }
        }
        if (tempTransactionIds.length > 0) {
            await sleep(2000)
            setTransactionIds(tempTransactionIds)
        }
        if (tempErrors.length > 0) {
            setErrors(tempErrors)
        }
        setIsMinting(false)
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="sm" fullWidth>
            <DropPaper noMargin title="Mint and Send PFPs">
                <DropAlert
                    severity="info"
                    title="Mint and Send PFPs from this drop without restrictions"
                    condition={true}
                >
                    This feature allows you to mint and send your PFPs to a
                    receiver of choice. It will ignore any whitelisting
                    restrictions, future start dates or prices set for the drop.
                    The PFP will still be minted randomly, no one has an
                    influence on the final result.{' '}
                    <b>
                        Depending on the amount of mints and potential mints
                        already in the queue, it can take a while until the
                        receiver gets their NFT(s).
                    </b>
                </DropAlert>
                <div className="my-5">
                    <InputField
                        variant="bordered"
                        classNames={{
                            ...inputStyles,
                        }}
                        label="Receiver"
                        fullWidth={true}
                        isDisabled={isMinting || errors}
                        value={receiver}
                        onChange={(e) => setReceiver(e.target.value)}
                        isInvalid={receiver.length === 0}
                    />
                </div>
                <div className="mt-5">
                    <InputField
                        variant="bordered"
                        classNames={{
                            ...inputStyles,
                        }}
                        label="Amount to Mint"
                        type="number"
                        fullWidth={true}
                        onWheel={(e) => e.target.blur()}
                        isDisabled={isMinting || errors}
                        value={totalAssetAmountToMint}
                        onChange={(e) =>
                            setTotalAssetAmountToMint(parseInt(e.target.value))
                        }
                        isInvalid={
                            !totalAssetAmountToMint ||
                            totalAssetAmountToMint <= 0
                        }
                        min={1}
                    />
                </div>
                <DropAlert
                    condition={errors}
                    title="An error occured!"
                    severity="error"
                >
                    <div>Error Message:</div>
                    {errors?.map((error) => (
                        <div>{error}</div>
                    ))}
                </DropAlert>
                <DropAlert
                    condition={transactionIds !== null}
                    severity="success"
                    title="Success!"
                >
                    Successfully executed transaction
                    {transactionIds?.length !== 1 ? 's' : ''}
                    {transactionIds?.map((transactionId) => (
                        <div>
                            <a
                                className="font-mono hover:underline"
                                href={
                                    (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'https://testnet.waxblock.io/transaction/'
                                        : 'https://waxblock.io/transaction/') +
                                    transactionId
                                }
                                target="_blank"
                            >
                                {transactionId}
                            </a>
                        </div>
                    ))}
                </DropAlert>
                {isMinting ? (
                    <div className="mt-10">
                        <div className="text-xl italic">
                            Loading Transactions - Adding wait time to avoid
                            Rate Limiting - Tx {currentDisplayAction} /{' '}
                            {Math.ceil(
                                (totalAssetAmountToMint -
                                    totalMintedSuccessfully) /
                                    MAX_MINTS_PER_TRANSACTION,
                            )}
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : (
                    // <div className="flex flex-wrap justify-center mt-10">
                    <div className="flex flex-col justify-center mt-10">
                        <MainButton
                            className="w-full mb-5"
                            fullWidth={true}
                            onClick={() => mintAndSendAssets()}
                            disabled={
                                (transactionIds && !errors) ||
                                receiver.length === 0 ||
                                totalAssetAmountToMint <= 0 ||
                                !totalAssetAmountToMint
                            }
                        >
                            {errors
                                ? `Retry Remaining ${
                                      totalAssetAmountToMint -
                                      totalMintedSuccessfully
                                  } mints`
                                : `Mint and send ${
                                      totalAssetAmountToMint
                                          ? totalAssetAmountToMint
                                          : ''
                                  } PFP${
                                      totalAssetAmountToMint !== 1 ? 's' : ''
                                  } to ${receiver ? receiver : '?'}`}
                        </MainButton>
                        <MainButton
                            className="w-full"
                            fullWidth={true}
                            color="danger"
                            onClick={() => close()}
                        >
                            {transactionIds === null
                                ? 'Cancel and return to overview'
                                : 'Close'}
                        </MainButton>
                    </div>
                )}
            </DropPaper>
        </Dialog>
    )
}
